import { ColumnSortConfig, ColumnViewConfig, ViewBase } from '../model';
import isEqual from 'lodash/isEqual';
import { FiltersMap, FiltersValues } from '@wix/bex-core';
import { ToolbarCollectionState } from './ToolbarCollectionState';
import { computed, makeObservable } from 'mobx';
import { CollectionViewsState } from './CollectionViewsState';

export interface CollectionViewsQueryCompareHelperParams<
  T,
  F extends FiltersMap,
> {
  table: ToolbarCollectionState<T, F>;
  collectionViews: CollectionViewsState<T, F>;
}

export class CollectionViewsQueryCompareHelper<T, F extends FiltersMap> {
  readonly collectionViews;
  readonly table;

  get views() {
    return this.collectionViews.views;
  }

  constructor(params: CollectionViewsQueryCompareHelperParams<T, F>) {
    this.table = params.table;
    this.collectionViews = params.collectionViews;

    makeObservable(this, {
      currentViewBase: computed,
      isSelectedColumnsChanged: computed,
      isSortChanged: computed,
      isFiltersChanged: computed,
      viewBaseSortDirections: computed,
      viewBaseSelectedColumns: computed,
      viewBaseFilters: computed,
      nonEmptyViewFiltersKeys: computed,
    });
  }

  get isSelectedColumnsChanged(): boolean {
    const { collectionViews } = this;

    return this.isColumnsChanged(
      collectionViews.currentViewColumnsOrDefaultColumns,
      this.viewBaseSelectedColumns,
    );
  }

  isColumnsChanged(
    columns1?: ColumnViewConfig[],
    columns2?: ColumnViewConfig[],
  ) {
    if (!columns1 || !columns2) {
      return false;
    }

    if (columns1.length !== columns2.length) {
      return true;
    }

    return columns1.some(
      (column, index) =>
        column.id !== columns2[index].id ||
        column.isSelected !== columns2[index].isSelected,
    );
  }

  get isSortChanged(): boolean {
    const currentView = this.views.currentView;
    if (!currentView) {
      return true;
    }
    const sortDirections = this.viewBaseSortDirections;
    return !currentView.sortDirections && !sortDirections
      ? false
      : currentView.sortDirections?.length !== sortDirections?.length ||
          (currentView.sortDirections ?? []).some(
            (sort, i) =>
              sortDirections?.[i].columnId !== sort.columnId ||
              sortDirections?.[i].direction !== sort.direction,
          );
  }

  get isFiltersChanged(): boolean {
    const currentView = this.views.currentView;
    if (!currentView) {
      return true;
    }

    const filters = this.viewBaseFilters;
    const filterKeys = this.nonEmptyViewFiltersKeys;
    return (
      filterKeys.length !== Object.keys(filters).length ||
      filterKeys.some((filterName) => {
        const currentViewFilter = currentView?.filters?.[filterName]?.value;
        const appliedFilter = filters?.[filterName]?.value;

        // equals accepts non empty filters
        if (currentViewFilter === undefined || appliedFilter === undefined) {
          return currentViewFilter !== appliedFilter;
        }

        const equals =
          this.table.collection.query.filters?.[filterName]?.equals ?? isEqual;

        return !equals(currentViewFilter, appliedFilter);
      })
    );
  }

  get nonEmptyViewFiltersKeys() {
    const currentView = this.views.currentView;
    if (!currentView) {
      return [];
    }
    return Object.keys(currentView.filters ?? {}).reduce((acc, filter) => {
      return this.table.collection.query.filters[filter]?.clone({
        initialValue: currentView.filters?.[filter]?.value,
      }).isEmpty
        ? acc
        : acc.concat(filter);
    }, [] as string[]);
  }

  get currentViewBase(): ViewBase<F> {
    return {
      filters: this.viewBaseFilters,
      sortDirections: this.viewBaseSortDirections,
      selectedColumns: this.viewBaseSelectedColumns,
    };
  }

  get viewBaseSortDirections() {
    const sortState = this.table.collection.query.sort;
    const sortDirections = sortState.value.map(
      (v) =>
        ({
          columnId: v.field,
          direction: v.direction === 'asc' ? 'Ascending' : 'Descending',
        } as ColumnSortConfig),
    );
    return sortDirections.length === 0 ? undefined : sortDirections;
  }

  get viewBaseSelectedColumns() {
    return this.table.selectedColumns.value;
  }

  get viewBaseFilters() {
    return this.table.collection.query.activeFiltersEntries.reduce(
      (acc, [filterName, filter]) => {
        return {
          ...acc,
          [filterName]: {
            value: filter.encode(filter.value),
          },
        };
      },
      {} as Partial<FiltersValues<F>>,
    );
  }
}
